import { trainCourseEntityName } from '../../backend/config/classifiers/entities/defineTrainCourseClassifier';
import { accountTrainCourseEntityName } from '../../backend/features/train-courses/shared';
import { useAccountFilter } from '@juvare/contact-management/ui/features/accounts/hooks/useAccountFilter';
import { findFilterValue, mapAccountFilterReverse, splitFilterValue, } from '@juvare/contact-management/ui/features/groups/utils/mapAccountFilterReverse';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { regionEntityName } from '@juvare/platform-sdk-react/backend/features/default-classifiers/shared';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { isTrainFeatureEnabled } from '../utils';
import { useFeatureFlags } from './useFeatureFlags';
export function useOverrideMapCredentialingAccountFilter() {
    const userState = useUserState();
    const { occupations: occupationEnabled, credential: credentialingEnabled, tribe: locationEnabled, deploymentAndPriorCommitments: deploymentEnabled, skillsAndTrainings: skillsAndTrainingEnabled, } = useFeatureFlags();
    const useOverrideCredentialingAccountFilter = (params) => {
        var _a, _b;
        const { data, error, isLoading } = useAccountFilter(params);
        const { filterData } = params;
        const { data: regionData, error: regionError } = useStorageList(regionEntityName, filterData.region
            ? {
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: filterData.region,
                    },
                ],
            }
            : undefined);
        if (error || regionError) {
            return { isLoading: false, error: error || regionError };
        }
        if (!data || isLoading) {
            return { data: undefined, isLoading: isLoading };
        }
        if (filterData === null || filterData === void 0 ? void 0 : filterData.stateAndCounty) {
            data.push({ column: 'stateAndCounty', op: 'in', value: filterData.stateAndCounty });
        }
        if (occupationEnabled) {
            if (filterData === null || filterData === void 0 ? void 0 : filterData.occupationGroup) {
                data.push({ column: 'occupationGroups', op: 'overlap', value: [filterData.occupationGroup] });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.occupationIds) {
                data.push({ column: 'occupationIds', op: 'overlap', value: filterData.occupationIds });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.credentialNames) {
                data.push({ column: 'credentialNames', op: 'overlap', value: filterData.credentialNames });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.licenseStatuses) {
                data.push({ column: 'licenseStatuses', op: 'overlap', value: filterData.licenseStatuses });
            }
            if (credentialingEnabled) {
                if (filterData === null || filterData === void 0 ? void 0 : filterData.ecls) {
                    data.push({ column: 'ecls', op: 'overlap', value: filterData.ecls });
                }
            }
        }
        if (locationEnabled) {
            if (filterData === null || filterData === void 0 ? void 0 : filterData.city) {
                data.push({ column: 'city', op: '=', value: filterData.city });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.postalCode) {
                data.push({ column: 'postalCode', op: '=', value: filterData.postalCode });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.state) {
                data.push({ column: 'state', op: '=', value: filterData.state });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.county) {
                data.push({ column: 'county', op: 'in', value: filterData.county });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.tribeId) {
                data.push({ column: 'tribeId', op: 'in', value: filterData.tribeId });
            }
            if ((filterData === null || filterData === void 0 ? void 0 : filterData.region) && ((_a = regionData === null || regionData === void 0 ? void 0 : regionData.rows[0]) === null || _a === void 0 ? void 0 : _a.columns.counties)) {
                data.push({
                    column: 'county',
                    op: 'in',
                    value: (_b = regionData === null || regionData === void 0 ? void 0 : regionData.rows[0]) === null || _b === void 0 ? void 0 : _b.columns.counties,
                });
            }
        }
        if (deploymentEnabled) {
            if (filterData === null || filterData === void 0 ? void 0 : filterData.deploymentDuration) {
                data.push({ column: 'deploymentDurationId', op: 'in', value: filterData.deploymentDuration });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.deploymentTravelIds) {
                data.push({ column: 'deploymentTravelIds', op: 'overlap', value: filterData.deploymentTravelIds });
            }
        }
        if (skillsAndTrainingEnabled) {
            if (filterData === null || filterData === void 0 ? void 0 : filterData.languageIds) {
                data.push({ column: 'languageIds', op: 'overlap', value: filterData.languageIds });
            }
            if (filterData === null || filterData === void 0 ? void 0 : filterData.skillIds) {
                data.push({ column: 'skillIds', op: 'overlap', value: filterData.skillIds });
            }
            if ((filterData === null || filterData === void 0 ? void 0 : filterData.trainingCourseIds) && (filterData === null || filterData === void 0 ? void 0 : filterData.trainingStatusSet)) {
                const courseIds = filterData.trainingCourseIds;
                const value = filterData.trainingStatusSet.flatMap((statusId) => courseIds.map((courseId) => courseId + '_' + statusId));
                data.push({ column: 'trainingStatus', op: 'overlap', value });
            }
            else {
                if (filterData === null || filterData === void 0 ? void 0 : filterData.trainingCourseIds) {
                    data.push({ column: 'trainingCourseIds', op: 'overlap', value: filterData.trainingCourseIds });
                }
                if (filterData === null || filterData === void 0 ? void 0 : filterData.trainingStatusSet) {
                    data.push({ column: 'trainingStatusSet', op: 'overlap', value: filterData.trainingStatusSet });
                }
            }
        }
        if (isTrainFeatureEnabled(userState)) {
            if (filterData === null || filterData === void 0 ? void 0 : filterData.trainCourseIds) {
                data.push({
                    op: 'in',
                    idSubFilter: {
                        entity: accountTrainCourseEntityName,
                        column: 'accountId',
                        filter: [
                            {
                                column: 'courseId',
                                op: 'in',
                                value: filterData.trainCourseIds,
                            },
                        ],
                    },
                });
            }
            if (filterData.trainCourseTrainId) {
                data.push({
                    op: 'in',
                    idSubFilter: {
                        entity: accountTrainCourseEntityName,
                        column: 'accountId',
                        filter: [
                            {
                                column: 'courseId',
                                op: 'in',
                                subFilter: {
                                    entity: trainCourseEntityName,
                                    filter: [
                                        {
                                            column: 'trainId',
                                            op: '=',
                                            value: parseInt(filterData.trainCourseTrainId),
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                });
            }
        }
        return { data: data, isLoading: false };
    };
    const overrideMapCredentialingAccountFilterReverse = (filters) => {
        var _a;
        const result = Object.assign({}, mapAccountFilterReverse(filters));
        if (filters.length > 0 && ((_a = filters[0].metadata) === null || _a === void 0 ? void 0 : _a.ui)) {
            return filters[0].metadata.ui;
        }
        result.stateAndCounty = findFilterValue(filters, 'stateAndCounty', 'in', 'array');
        if (occupationEnabled) {
            result.occupationGroup = findFilterValue(filters, 'occupationGroups', 'overlap', 'array-of-one');
            result.occupationIds = findFilterValue(filters, 'occupationIds', 'overlap', 'array');
            result.credentialNames = findFilterValue(filters, 'credentialNames', 'overlap', 'array');
            result.licenseStatuses = findFilterValue(filters, 'licenseStatuses', 'overlap', 'array');
            if (credentialingEnabled) {
                result.ecls = findFilterValue(filters, 'ecls', 'overlap', 'array');
            }
        }
        if (locationEnabled) {
            result.city = findFilterValue(filters, 'city', '=', 'string');
            result.postalCode = findFilterValue(filters, 'postalCode', '=', 'string');
            result.state = findFilterValue(filters, 'state', '=', 'string');
            result.county = findFilterValue(filters, 'county', 'in', 'array');
            result.tribeId = findFilterValue(filters, 'tribeId', 'in', 'array');
        }
        if (deploymentEnabled) {
            result.deploymentDuration = findFilterValue(filters, 'deploymentDurationId', 'in', 'array');
            result.deploymentTravelIds = findFilterValue(filters, 'deploymentTravelIds', 'overlap', 'array');
        }
        if (skillsAndTrainingEnabled) {
            result.languageIds = findFilterValue(filters, 'languageIds', 'overlap', 'array');
            result.skillIds = findFilterValue(filters, 'skillIds', 'overlap', 'array');
            result.trainingStatus = findFilterValue(filters, 'trainingStatus', 'overlap', 'array');
            if (result.trainingStatus) {
                const trainingCourseIds = new Set();
                const trainingStatusSet = new Set();
                result.trainingStatus.forEach((value) => {
                    const [courseIds, courseStatus] = splitFilterValue(value);
                    trainingCourseIds.add(courseIds);
                    trainingStatusSet.add(courseStatus);
                });
                result.trainingCourseIds = trainingCourseIds.size > 0 ? Array.from(trainingCourseIds) : undefined;
                result.trainingStatusSet = trainingStatusSet.size > 0 ? Array.from(trainingStatusSet) : undefined;
            }
            else {
                result.trainingCourseIds = findFilterValue(filters, 'trainingCourseIds', 'overlap', 'array');
                result.trainingStatusSet = findFilterValue(filters, 'trainingStatusSet', 'overlap', 'array');
            }
        }
        return result;
    };
    return { useOverrideCredentialingAccountFilter, overrideMapCredentialingAccountFilterReverse };
}
